import React, { useCallback, useEffect, useState, useMemo } from "react";
import WebAppContainer from "@/components/WebAppContainer/WebAppContainer";
import Typography from "@/ui/Typography/Typography";
import { AgglomerationI, Regions } from "@/types/base";
import { useAgglomeration } from "@/store/agglomeration";
import { useAgglomerationsDataContext } from "@/store/AgglomerationsProvider";
import ContainerLoader from "@/ui/ContainerLoader/ContainerLoader";
import { useUserDataContext } from "@/store/UserProvider";
import { useHexGroupByLocation } from "@/utils/hooks/useHexGroupByLocation";
import { changeUserHexGroupId, changeUserAgglomerationId } from "@/api/user";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { useSearchHistory } from "@/store/citySearchHistory";
import { useTariffLayer } from "@/store/mode";
import { SearchHistoryIcon, SmallArrowIcon, CloseIcon } from "@/assets/icons";
import { useChooseRegionStore } from "@/store/chooseRegion";
import { useTranslation } from "react-i18next";
import cls from "./styles.module.scss";

export default function AgglomerationSelector() {
  const { t } = useTranslation();
  const {
    hexGroupByLocation,
    hexGroupByLocationLoading,
  } = useHexGroupByLocation();
  const { user, userLoading } = useUserDataContext();

  const { searchHistory, setSearchHistory } = useSearchHistory();
  const { setChooseRegion } = useChooseRegionStore();
  const serviceLayer = useTariffLayer((s) => s.serviceLayer);

  const {
    agglomerations,
    agglomerationsLoading,
  } = useAgglomerationsDataContext();
  const initialAgglomerationIsSet = useAgglomeration(
    (s) => s.initialAgglomerationIsSet
  );

  const currentHexGroup = useSelectedHexGroupStore((s) => s.currentHexGroup);
  const selectedHexGroup = useSelectedHexGroupStore((s) => s.selectedHexGroup);
  const setSelectedHexGroup = useSelectedHexGroupStore(
    (s) => s.setSelectedHexGroup
  );
  const { setSelectedAgglomeration } = useAgglomeration();

  const regions: Regions[] = agglomerations?.flatMap((agglomeration) =>
    agglomeration.hexgroups
      .filter((hexgroup) =>
        serviceLayer === "eda" ? Boolean(hexgroup.is_eda_active) : true
      )
      .map((hexgroup) => ({
        agglomeration_id: agglomeration.id,
        agglomeration_name: agglomeration?.name,
        hexgroup_id: hexgroup.id,
        hexgroup_name: hexgroup?.name,
      }))
  );

  const availableRegionIds = useMemo(
    () => new Set(regions.map((region) => region.hexgroup_id)),
    [regions]
  );

  const backToMap = () => {
    if (!selectedHexGroup) {
      setSelectedHexGroup(currentHexGroup);
    }
    setChooseRegion(false);
  };

  const [searchTerm, setSearchTerm] = useState<string>("");

  const filteredRegions = useMemo(() => {
    const priorityMap = new Map([
      ["москва", 0],
      ["санкт-петербург", 1],
    ]);

    const sortedRegions = [...regions].sort((a, b) => {
      const aName = a.hexgroup_name.toLowerCase();
      const bName = b.hexgroup_name.toLowerCase();

      const aPriority = priorityMap.get(aName);
      const bPriority = priorityMap.get(bName);

      if (aPriority !== undefined || bPriority !== undefined) {
        if (aPriority === undefined) return 1;
        if (bPriority === undefined) return -1;
        return aPriority - bPriority;
      }

      return a.hexgroup_name.localeCompare(b.hexgroup_name);
    });

    if (!searchTerm.trim()) return sortedRegions;

    const query = searchTerm.toLowerCase().trim();

    return sortedRegions.filter((region) =>
      region.hexgroup_name.toLowerCase().startsWith(query)
    );
  }, [regions, searchTerm]);

  const [userGroupNotFound, setUserGroupNotFound] = useState(false);

  const onConfirm = (
    agglomeration: AgglomerationI | undefined,
    hexgroupId: number,
    region?: Regions
  ) => {
    if (region) {
      setSearchHistory(region);
    }

    if (agglomeration) {
      setSelectedAgglomeration(agglomeration);
      changeUserAgglomerationId({ agglomerationId: agglomeration.id });
    }

    const userHexGroup = agglomeration?.hexgroups?.find(
      (h) => h.id === hexgroupId
    );

    if (userHexGroup) {
      setSelectedHexGroup(userHexGroup);
      changeUserHexGroupId({ hexGroupId: hexgroupId });
    }

    setChooseRegion(false);
  };

  const searchForGroup = useCallback(() => {
    if (initialAgglomerationIsSet) return;
    if (userLoading) return;
    if (agglomerationsLoading) return;

    if (user?.hexgroup_id) {
      const group = agglomerations?.find((item) =>
        item.hexgroups?.some((hexgroup) => hexgroup.id === user?.hexgroup_id)
      );
      if (group) {
        onConfirm(group, user?.hexgroup_id);
        return;
      }
    }

    if (hexGroupByLocation) {
      const groupByLoc = agglomerations.find((item) =>
        item.hexgroups?.some(
          (hexgroup) => hexgroup.id === hexGroupByLocation.id
        )
      );
      if (groupByLoc) {
        onConfirm(groupByLoc, hexGroupByLocation.id);
        return;
      }
    }

    if (!userLoading && !hexGroupByLocationLoading) {
      setUserGroupNotFound(true);
    }
  }, [
    user,
    initialAgglomerationIsSet,
    hexGroupByLocation,
    agglomerations,
    userLoading,
    hexGroupByLocationLoading,
  ]);

  useEffect(() => {
    searchForGroup();
  }, [searchForGroup]);

  const isLoading =
    agglomerationsLoading || userLoading || hexGroupByLocationLoading;

  if (isLoading || (!userGroupNotFound && !initialAgglomerationIsSet))
    return <ContainerLoader />;

  return (
    <WebAppContainer className={cls.wrapper}>
      <div className={cls.headerBlock}>
        {user?.agglomeration_id && user?.hexgroup_id && (
          <button
            type="button"
            className={cls.btnArrow}
            onClick={() => backToMap()}
          >
            <SmallArrowIcon />
          </button>
        )}
        <Typography className={cls.title}>
          {t("changeCity.searchCity")}
        </Typography>
      </div>
      <div className={cls.inputBlock}>
        <input
          type="text"
          placeholder={t("changeCity.city")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={cls.search}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            className={cls.closeIcon}
          >
            <CloseIcon />
          </button>
        )}
      </div>
      <div className={cls.list}>
        {filteredRegions.map((elem) => {
          const agglomeration = agglomerations?.find(
            (g) => g.id === elem.agglomeration_id
          );
          return (
            <button
              type="button"
              onClick={() => onConfirm(agglomeration, elem.hexgroup_id, elem)}
              className={cls.item}
              key={elem.hexgroup_id}
            >
              <div className={cls.region}>
                <div className={cls.hexgroupName}>{elem.hexgroup_name}</div>
                <div className={cls.agglomerationName}>
                  {elem.agglomeration_name}
                </div>
              </div>
            </button>
          );
        })}
        {searchHistory &&
          !filteredRegions.length &&
          !searchTerm &&
          searchHistory
            .filter((elem) => availableRegionIds.has(elem.hexgroup_id))
            .map((elem) => {
              const agglomeration = agglomerations?.find(
                (g) => g.id === elem.agglomeration_id
              );

              return (
                <button
                  type="button"
                  onClick={() =>
                    onConfirm(agglomeration, elem.hexgroup_id, elem)}
                  className={cls.item}
                  key={elem.hexgroup_id}
                >
                  <SearchHistoryIcon />
                  <div className={cls.region}>
                    <div className={cls.hexgroupName}>{elem.hexgroup_name}</div>
                    <div className={cls.agglomerationName}>
                      {elem.agglomeration_name}
                    </div>
                  </div>
                </button>
              );
            })}
        {!filteredRegions.length && searchTerm && (
          <div className={cls.notFound}>{t("changeCity.nothingFound")}</div>
        )}
      </div>
    </WebAppContainer>
  );
}
